import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import { Card, CardHeader, Button, TextInput, Textarea, Breadcrumbs, PageHeader, Alert, Globe01 } from "@opendate-app/setlist";
import SkillsInput from "./components/SkillsInput";
import InstructorPhotoField from "./components/InstructorPhotoField";
import { errorText, toastError } from "../../shared/toastHelper";
import ValidatedSelect from "../ClassesShared/ValidatedSelect";
import focusField from "../ClassesShared/focusField";
import { mergeFanIntoForm } from "./fanAutofill";
import "../ClassesShared/classesShared.css";
import "./instructors.css";

const FIELDS = ["name", "title", "email", "phone", "website_url", "instagram_handle", "bio"];

function InstructorFormApp(props) {
  const { instructor, createPath, updatePath, indexPath, csrfToken, breadcrumb } = props;
  const isEditing = !!instructor;

  const [form, setForm] = useState(() => {
    const base = { skills: instructor?.skills || [] };
    FIELDS.forEach((f) => {
      base[f] = instructor?.[f] || "";
    });
    return base;
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(instructor?.photo_url || null);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [nameError, setNameError] = useState(null);
  const [focusRequest, setFocusRequest] = useState(null);

  useEffect(() => {
    if (focusRequest) focusField(focusRequest.field);
  }, [focusRequest]);

  const replaceImageUrl = (next) => {
    setImageUrl((previous) => {
      if (previous && previous.startsWith("blob:")) URL.revokeObjectURL(previous);
      return next;
    });
  };

  const setField = (key, val) => {
    if (key === "name") setNameError(null);
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const fansByValue = useRef(new Map());

  const loadFanOptions = (input) => {
    if (!input || input.trim().length < 2) return Promise.resolve([]);
    return axios
      .get(props.fansAutocompletePath, { params: { name: input } })
      .then(({ data }) => data.map((fan) => {
        const value = `fan:${fan.id}`;
        fansByValue.current.set(value, fan);
        return { value, label: fan.email ? `${fan.name} (${fan.email})` : fan.name };
      }))
      .catch((e) => {
        toastError(errorText(e, "Could not load fans."));
        return [];
      });
  };

  const handleNameSelect = (value) => {
    const fan = value && fansByValue.current.get(value);
    if (fan) {
      setNameError(null);
      setForm((prev) => mergeFanIntoForm(prev, fan));
    } else {
      setField("name", value || "");
    }
  };

  const submit = async () => {
    if (saving) return;
    if (form.name.trim() === "") {
      setNameError("Give the instructor a name.");
      setFocusRequest({ field: "instructor-name" });
      return;
    }
    setSaving(true);
    setErrorMessage(null);

    const data = new FormData();
    FIELDS.forEach((f) => data.append(`instructor[${f}]`, form[f] || ""));
    if (form.skills.length === 0) {
      data.append("instructor[skills][]", "");
    } else {
      form.skills.forEach((s) => data.append("instructor[skills][]", s));
    }
    if (photoFile) data.append("instructor[photo]", photoFile);
    if (isEditing && !imageUrl && !photoFile) data.append("instructor[remove_photo]", "true");

    const headers = { "X-CSRF-Token": csrfToken };
    try {
      if (updatePath) {
        await axios.patch(updatePath, data, { headers });
      } else {
        await axios.post(createPath, data, { headers });
      }
      window.location.href = updatePath || indexPath;
    } catch (e) {
      setErrorMessage(errorText(e, "Could not save instructor."));
      setSaving(false);
    }
  };

  return (
    <div className="od-themed p-4">
      <PageHeader
        breadcrumbs={breadcrumb && breadcrumb.length > 0 ? <Breadcrumbs items={breadcrumb} /> : undefined}
        title={<span role="heading" aria-level="1">{isEditing ? "Edit Instructor" : "New Instructor"}</span>}
        supportingText="Instructors are who teaches your classes. Profiles appear on public class pages."
        actions={(
          <div className="d-flex align-items-center classes-gap-12">
            <Button as="a" variant="tertiary" href={indexPath} aria-disabled={saving || undefined} onClick={saving ? (e) => e.preventDefault() : undefined}>Cancel</Button>
            <Button variant="primary" onClick={submit} aria-disabled={saving || undefined}>{saving ? "Saving…" : "Save Instructor"}</Button>
          </div>
        )}
      />

      <div className="instructor-form classes-form mt-4">
        <h2 className="sr-only">Instructor details</h2>
        {errorMessage && (
          <div className="mb-3">
            <Alert color="error" role="alert" title="Could not save instructor" description={errorMessage} />
          </div>
        )}

        <div className="instructor-form__cards">
          <Card header={<CardHeader title="Profile" supportingText="Basics shown to learners on public class pages." />}>
            <InstructorPhotoField
              imageUrl={imageUrl}
              name={form.name}
              onUpload={(file) => {
                setPhotoFile(file);
                replaceImageUrl(URL.createObjectURL(file));
              }}
              onRemove={() => {
                setPhotoFile(null);
                replaceImageUrl(null);
              }}
            />

            <div className="classes-form-row mt-4">
              <ValidatedSelect
                id="instructor-name"
                label="Name"
                error={nameError}
                required
                searchable
                clearable
                createOnBlur
                placeholder="Search contacts or type a new name…"
                formatCreateLabel={(input) => `Add "${input}" as a new instructor`}
                noOptionsMessage="Type at least 2 letters to search your contacts"
                loadOptions={loadFanOptions}
                options={form.name ? [{ value: form.name, label: form.name }] : []}
                value={form.name || null}
                onChange={handleNameSelect}
                onCreateOption={(input) => setField("name", input)}
              />
              <TextInput
                label="Email"
                type="email"
                hint="Used for assignment notifications later."
                placeholder="instructor@email.com"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
              />
            </div>

            <div className="classes-form-row mt-4">
              <TextInput
                label="Title"
                placeholder="e.g. Resident DJ"
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
              />
              <TextInput
                label="Phone"
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
              />
            </div>

            <div className="mt-4">
              <Textarea
                label="Bio"
                rows={4}
                placeholder="A short bio learners will see on the public class page. Their teaching style, experience, what they bring to the room."
                value={form.bio}
                onChange={(e) => setField("bio", e.target.value)}
              />
            </div>
          </Card>

          <Card header={<CardHeader title="Skills" supportingText="Used for filtering when you assign instructors to sessions." />}>
            <SkillsInput value={form.skills} onChange={(skills) => setField("skills", skills)} />
          </Card>

          <Card header={<CardHeader title="Links (Optional)" />}>
            <div className="classes-form-row">
              <TextInput
                label="Website"
                type="url"
                leadingIcon={Globe01}
                placeholder="https://"
                value={form.website_url}
                onChange={(e) => setField("website_url", e.target.value)}
              />
              <TextInput
                label="Instagram"
                placeholder="@handle"
                value={form.instagram_handle}
                onChange={(e) => setField("instagram_handle", e.target.value)}
              />
            </div>
          </Card>
        </div>
      </div>

      <ToastContainer icon={false} />
    </div>
  );
}

InstructorFormApp.propTypes = {
  instructor: PropTypes.shape({
    name: PropTypes.string,
    title: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    website_url: PropTypes.string,
    instagram_handle: PropTypes.string,
    bio: PropTypes.string,
    skills: PropTypes.arrayOf(PropTypes.string),
    photo_url: PropTypes.string,
  }),
  createPath: PropTypes.string,
  updatePath: PropTypes.string,
  indexPath: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  breadcrumb: PropTypes.array,
  fansAutocompletePath: PropTypes.string.isRequired,
};

InstructorFormApp.defaultProps = {
  instructor: null,
};

export default InstructorFormApp;
